import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiLock, FiPhone, FiEye, FiEyeOff, FiArrowRight, FiCheck } from 'react-icons/fi';
import { GiCompass } from 'react-icons/gi';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Auth.css';

export default function Register() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
    if (result.success) {
      toast.success('Account created! Welcome to The Unexplored Routes!');
      navigate('/dashboard');
    } else {
      toast.error(result.message || 'Registration failed');
    }
    setLoading(false);
  };

  const passwordStrength = form.password.length === 0 ? 0 : form.password.length < 6 ? 1 : form.password.length < 10 ? 2 : 3;
  const strengthLabel = ['', 'Weak', 'Good', 'Strong'];
  const strengthColor = ['', '#e53e3e', '#ed8936', '#48bb78'];

  return (
    <div className="auth-page">
      <div className="auth-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1609766857898-b1d35a6c26ae?w=1920&h=1080&fit=crop)' }} />
      <div className="auth-overlay" />
      <div className="auth-container">
        <motion.div className="auth-card register-card" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="auth-logo">
            <GiCompass />
            <div>
              <span>The Unexplored</span>
              <small>Routes</small>
            </div>
          </div>
          <h2>Create Account</h2>
          <p>Join us and start your offbeat journey</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <FiUser className="field-icon" />
              <input type="text" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="auth-field">
              <FiMail className="field-icon" />
              <input type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="auth-field">
              <FiPhone className="field-icon" />
              <input type="tel" placeholder="Phone number (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="auth-field">
              <FiLock className="field-icon" />
              <input type={showPassword ? 'text' : 'password'} placeholder="Create password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              <button type="button" className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {form.password && (
              <div className="password-strength">
                <div className="strength-bars">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="strength-bar" style={{ background: i <= passwordStrength ? strengthColor[passwordStrength] : '#e2e8f0' }} />
                  ))}
                </div>
                <span style={{ color: strengthColor[passwordStrength], fontSize: '0.78rem' }}>{strengthLabel[passwordStrength]}</span>
              </div>
            )}
            <div className="auth-field">
              <FiLock className="field-icon" />
              <input type={showPassword ? 'text' : 'password'} placeholder="Confirm password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required />
              {form.confirmPassword && form.password === form.confirmPassword && <FiCheck className="field-match" />}
            </div>
            <button type="submit" className="btn-primary auth-submit" disabled={loading}>
              {loading ? 'Creating Account...' : <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Create Account <FiArrowRight /></span>}
            </button>
          </form>

          <p className="auth-switch">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
